// Google Ads conversion tracking.
//
// This used to be hardcoded twice over (#1255): the base gtag.js loader rendered only
// for `name === "EASYTIX"`, and every conversion snippet carried the literal EasyTix tag
// `AW-17452313366`. So nimi.co.il loaded no tag at all — three conversion actions sat
// `Misconfigured` in the Nimi Ads account while budget was being spent blind — and any
// conversion that did fire reported into EasyTix's account.
//
// Everything is per-marketplace config now (`googleAds` in bootstrapData). A marketplace
// without that block loads no Google tag and reports nothing, which is what every
// white-label wants.

// Ads account id, e.g. AW-17452313366. Deliberately narrow: a GTM container (GTM-…) or a
// GA4 measurement id (G-…) in this field would load a tag that reports no conversions.
const AW_ID = /^AW-\d+$/;
// A conversion label as the Ads UI hands it over: "<account>/<label>".
const SEND_TO = /^AW-\d+\/[\w-]+$/;

/** Whether this marketplace has a usable Google Ads account configured. */
export const isValidAdsConfig = (config) =>
    !!config && typeof config.id === 'string' && AW_ID.test(config.id);

/** The gtag.js loader URL for a marketplace, or null when it has no Ads account. */
export const getGtagSrc = (config) =>
    isValidAdsConfig(config) ? `https://www.googletagmanager.com/gtag/js?id=${config.id}` : null;

/**
 * The `send_to` for one conversion key, or null when it isn't configured.
 *
 * A label whose account prefix doesn't match this marketplace's own id is refused: that
 * mismatch is exactly how nimi.co.il ended up reporting into the EasyTix account, and it
 * is silent in the Ads UI — the conversion simply shows up somewhere else.
 */
export const resolveConversionTarget = (config, key) => {
    if (!isValidAdsConfig(config)) return null;

    const sendTo = config.conversions?.[key];
    if (typeof sendTo !== 'string' || !SEND_TO.test(sendTo)) return null;

    if (!sendTo.startsWith(`${config.id}/`)) {
        console.warn(
            `[googleAds] conversion "${key}" (${sendTo}) belongs to another Ads account than ${config.id} — not reported`
        );
        return null;
    }
    return sendTo;
};

/**
 * Report one conversion, then run `callback`.
 *
 * `callback` is how the WhatsApp button navigates, so it must run exactly once and it must
 * run whatever happens — no Ads account, no gtag on the page, an ad-blocker, or a gtag that
 * simply never answers. Google's own snippet has the same shape; the timeout is the part
 * people leave out, and without it a blocked callback swallows the click entirely.
 *
 * @returns {boolean} whether the conversion was actually handed to gtag.
 */
export const reportConversion = (config, key, { callback, timeoutMs = 1000 } = {}) => {
    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        callback?.();
    };

    const sendTo = resolveConversionTarget(config, key);
    if (!sendTo || typeof window === 'undefined' || typeof window.gtag !== 'function') {
        finish();
        return false;
    }

    try {
        window.gtag('event', 'conversion', { send_to: sendTo, event_callback: finish });
    } catch (e) {
        console.error(e);
        finish();
        return false;
    }

    setTimeout(finish, timeoutMs);
    return true;
};
